import { Eye, FileText, IndianRupee, Info, ShieldCheck } from "lucide-react";
import type { ReactNode } from "react";
import { useRef, useState } from "react";
import Button from "@/components/ui/Button";
import Checkbox from "@/components/ui/Checkbox";
import Modal from "@/components/ui/Modal";
import { WALLET_RATES } from "@/pages/retailer/wallet/constants";
import { maskAccount, maskAadhaar } from "@/components/feature/mandates/enrichMandates";
import { ADDRESS_PROOF_IMAGE, ID_PROOF_IMAGE } from "@/components/feature/mandates/docImages";
import { computeEmi, formatMoney } from "../emi";
import type { MandateForm, SetField } from "../types";
import { usePlatformSettings } from "@/services/platformSettings";

interface Props {
  form: MandateForm;
  onEdit: (step: number) => void;
  confirmChecked: boolean;
  onConfirmChange: (v: boolean) => void;
  setField: SetField;
  balanceAfter: number;
  mpinError?: string;
  creationCharge?: number;
  installmentCharge?: number;
}

function DefRow({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="shrink-0 text-[13px] text-ink-secondary">{label}</span>
      <span className={`text-right ${highlight ? "text-base font-bold text-primary-500" : "text-sm font-medium text-ink"}`}>
        {value}
      </span>
    </div>
  );
}

function Section({ title, onEdit, children }: { title: string; onEdit: () => void; children: ReactNode }) {
  return (
    <div className="overflow-hidden rounded-[10px] border border-line bg-white">
      <div className="flex items-center justify-between bg-page px-5 py-3">
        <span className="text-sm font-semibold text-ink">{title}</span>
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1.5 text-[13px] font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
        >
          <FileText className="h-3.5 w-3.5" />
          Edit
        </button>
      </div>
      <div className="divide-y divide-[#F1F5F9] px-5 py-2">{children}</div>
    </div>
  );
}

const TERMS = [
  "The customer has authorised the retailer to submit these mandate details on their behalf.",
  "The information and documents provided are accurate, complete and up to date.",
  "EMI amounts will be debited automatically from the customer's bank account on the scheduled dates.",
  "The mandate can be cancelled by the customer or the retailer at any time through the dashboard.",
  "The platform charges a one-time mandate creation fee and a per-installment handling charge as displayed.",
  "The retailer is responsible for the correctness of the product, EMI and customer details entered.",
  "Failed or bounced installments may be retried; applicable bank charges may apply.",
  "The platform may verify documents and share details with partner banks and payment providers.",
];

export default function ReviewStep({
  form,
  onEdit,
  confirmChecked,
  onConfirmChange,
  setField,
  balanceAfter,
  mpinError,
  creationCharge = WALLET_RATES.CREATION_CHARGE,
  installmentCharge = WALLET_RATES.INSTALLMENT_CHARGE,
}: Props) {
  const platformSettings = usePlatformSettings();
  const [termsOpen, setTermsOpen] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [preview, setPreview] = useState<{ title: string; url?: string; fileName?: string } | null>(null);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const emi = computeEmi(form.productPrice, form.downPayment, form.interestRate, form.tenure);
  const digits = Array.from({ length: 4 }, (_, i) => form.mpin[i] ?? "");
  const mpinIncomplete = form.mpin.length < 4;

  function setDigit(i: number, v: string) {
    const val = v.replace(/\D/g, "").slice(-1);
    const next = form.mpin.split("");
    next[i] = val;
    setField("mpin", next.join(""));
    if (val && i < 3) inputRefs.current[i + 1]?.focus();
  }

  function onKeyDown(i: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace" && !form.mpin[i] && i > 0) {
      inputRefs.current[i - 1]?.focus();
    }
  }

  const verificationTiles = [
    { title: "Customer Photo", url: form.customerPhoto, fileName: "Photograph" },
    { title: form.idProofType || "ID Proof", url: form.idProofUrl, fileName: form.idProofFileName },
    { title: form.addressProofType || "Address Proof", url: form.addressProofUrl, fileName: form.addressProofFileName },
    { title: "Signature", url: form.signature, fileName: "Signature" },
  ];

  return (
    <div>
      <h3 className="text-lg font-semibold text-ink">Review &amp; Submit</h3>
      <p className="text-[13px] text-ink-secondary">
        Please verify all details before generating the authorisation link
      </p>

      <div className="mt-6 flex flex-col gap-4">
        <Section title="Customer Details" onEdit={() => onEdit(1)}>
          <DefRow label="Customer Name" value={form.customerName || "—"} />
          <DefRow label="Mobile Number" value={form.customerMobile ? `+91 ${form.customerMobile}` : "—"} />
          <DefRow label="Email" value={form.customerEmail || "—"} />
          <DefRow label="Date of Birth" value={form.dob || "—"} />
          <DefRow label="PAN" value={form.customerPan || "—"} />
          <DefRow label="Aadhaar" value={maskAadhaar(form.aadhaar)} />
          <DefRow label="Address" value={form.address || "—"} />
          <DefRow label="City / State / Pincode" value={[form.city, form.state, form.pincode].filter(Boolean).join(", ") || "—"} />
        </Section>

        <Section title="Product Details" onEdit={() => onEdit(2)}>
          <DefRow label="Category" value={form.category || "—"} />
          <DefRow label="Brand" value={form.brand || "—"} />
          <DefRow label="Model" value={form.model || "—"} />
          <DefRow label="Serial / IMEI" value={form.serialNo || "—"} />
          <DefRow label="Invoice Number" value={form.invoiceNo || "—"} />
          <DefRow label="Invoice Date" value={form.invoiceDate || "—"} />
          <DefRow label="Description" value={form.description || "—"} />
        </Section>

        <Section title="EMI & Mandate" onEdit={() => onEdit(3)}>
          <DefRow label="Product Price" value={form.productPrice ? formatMoney(Number(form.productPrice)) : "—"} />
          <DefRow label="Down Payment" value={form.downPayment ? formatMoney(Number(form.downPayment)) : "—"} />
          <DefRow label="Financed Amount" value={emi ? formatMoney(emi.financed) : "—"} />
          <DefRow label="Interest Rate" value={form.interestRate ? `${form.interestRate}% p.a.` : "—"} />
          <DefRow label="Tenure" value={form.tenure ? `${form.tenure} months` : "—"} />
          <DefRow label="Total Interest" value={emi ? formatMoney(emi.interest) : "—"} />
          <DefRow label="Total Payable" value={emi ? formatMoney(emi.payable) : "—"} />
          <div className="py-2">
            <div className="h-px bg-slate-200" />
          </div>
          <DefRow label="Monthly EMI" value={form.amount ? formatMoney(Number(form.amount)) : "—"} highlight />
          <DefRow
            label="Authentication Mode"
            value={form.authMode === "UPI_AUTO_PAY" ? "UPI Autopay" : "eNACH"}
          />
          <DefRow label="Maximum Amount" value={form.maxAmount ? formatMoney(Number(form.maxAmount)) : "—"} />
          <DefRow label="Frequency" value={form.frequency || "—"} />
          <DefRow label="First Installment" value={form.startDate || "—"} />
          <DefRow label="End Date" value={form.endDate || "—"} />
          <DefRow label="Purpose" value={form.purpose || "—"} />
        </Section>

        {form.authMode === "UPI_AUTO_PAY" ? (
          <div className="flex items-start gap-3 rounded-[10px] border border-[#BAE6FD] bg-[#F0F9FF] p-4">
            <Info className="mt-0.5 h-5 w-5 shrink-0 text-info" />
            <p className="text-[13px] text-ink">
              The customer will select their UPI ID on the authorisation page. No bank details are required.
            </p>
          </div>
        ) : (
          <Section title="Bank Details" onEdit={() => onEdit(4)}>
            <DefRow label="Bank Name" value={form.bankName || "—"} />
            <DefRow label="Account Type" value={form.accountType} />
            <DefRow label="Account Number" value={maskAccount(form.accountNumber)} />
            <DefRow label="IFSC Code" value={form.ifsc || "—"} />
          </Section>
        )}

        <Section title="Verification" onEdit={() => onEdit(5)}>
          <div className="grid grid-cols-2 gap-3 py-3 sm:grid-cols-4">
            {verificationTiles.map((tile) => {
              const isPdf = tile.url?.startsWith("data:application/pdf") || tile.fileName?.toLowerCase().endsWith(".pdf");
              const hasImg = Boolean(tile.url && !isPdf);
              return (
                <div key={tile.title} className="group relative">
                  <div className="relative h-[100px] overflow-hidden rounded-lg border border-line bg-white">
                    {hasImg ? (
                      <img src={tile.url!} alt={tile.title} className="h-full w-full object-cover" />
                    ) : tile.fileName ? (
                      <div className="flex h-full w-full flex-col items-center justify-center p-2 bg-page text-center">
                        <FileText className="h-6 w-6 text-primary-500 mb-1" />
                        <p className="max-w-full truncate text-[11px] font-medium text-ink">{tile.fileName}</p>
                      </div>
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-page text-xs text-ink-muted">Not captured</div>
                    )}
                    <button
                      type="button"
                      onClick={() => (tile.url || tile.fileName) && setPreview({ title: tile.title, url: tile.url, fileName: tile.fileName })}
                      disabled={!tile.url && !tile.fileName}
                      className="absolute bottom-1.5 right-1.5 flex h-7 w-7 items-center justify-center rounded-md bg-white/90 text-ink-muted hover:text-ink disabled:opacity-40 cursor-pointer"
                      aria-label={`Preview ${tile.title}`}
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mt-1.5 text-xs text-ink-secondary">{tile.title}</p>
                </div>
              );
            })}
          </div>
        </Section>
      </div>

      {/* Charges notice */}
      <div className="mt-5 rounded-[10px] border border-amber-200 bg-warning-bg p-4">
        <div className="flex items-center gap-2">
          <IndianRupee className="h-5 w-5 text-warning" />
          <span className="text-sm font-semibold text-ink">Applicable charges</span>
        </div>
        <ul className="mt-2.5 space-y-1.5 text-[13px] text-ink">
          <li className="flex items-start gap-2">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-ink-muted" />
            One-time mandate charge: {formatMoney(creationCharge)}, deducted from your wallet on submission
          </li>
          <li className="flex items-start gap-2">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-ink-muted" />
            Handling charge per installment: {formatMoney(installmentCharge)}, deducted when each installment is successfully collected
          </li>
        </ul>
        <p className="mt-2.5 text-xs text-ink-secondary">
          Your available balance after this mandate will be {formatMoney(balanceAfter)}
        </p>
      </div>

      {/* Terms checkbox */}
      <div className="mt-5">
        <Checkbox
          checked={confirmChecked}
          onChange={(e) => onConfirmChange(e.target.checked)}
          label={
            <>
              I agree to the{" "}
              <button
                type="button"
                onClick={() => setTermsOpen(true)}
                className="font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
              >
                Terms and Conditions
              </button>{" "}
              and confirm that the customer has authorised me to submit these details. All information and documents provided are accurate.
            </>
          }
        />
      </div>

      {/* MPIN */}
      <div id="mpin" className="mt-6 rounded-[10px] border border-line bg-white p-5">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary-500" />
          <span className="text-sm font-semibold text-ink">Security Verification</span>
        </div>
        <p className="mt-1 text-xs text-ink-secondary">Enter your 4-digit MPIN to authorise this mandate</p>

        <div className="mt-5 flex items-center justify-center gap-3">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={(el) => {
                inputRefs.current[i] = el;
              }}
              value={d}
              inputMode="numeric"
              maxLength={1}
              type="password"
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              className={`h-[52px] w-[52px] rounded-lg border text-center text-xl text-ink focus:outline-none focus:ring-2 ${
                mpinError
                  ? "border-danger focus:ring-danger/30"
                  : "border-slate-300 focus:border-primary-500 focus:ring-primary-500/30"
              }`}
              aria-label={`MPIN digit ${i + 1}`}
            />
          ))}
        </div>

        {mpinError ? (
          <p className="mt-3 text-center text-xs text-danger">{mpinError}</p>
        ) : mpinIncomplete ? (
          <p className="mt-3 text-center text-xs text-ink-muted">Enter all 4 digits</p>
        ) : null}

        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={() => setForgotOpen(true)}
            className="text-xs font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
          >
            Forgot MPIN?
          </button>
        </div>
      </div>

      {/* Terms modal */}
      <Modal open={termsOpen} onClose={() => setTermsOpen(false)} title="Terms and Conditions" size="md" footer={<div className="flex items-center justify-end border-t border-line px-5 py-4"><Button variant="secondary" onClick={() => setTermsOpen(false)}>Close</Button></div>}>
        <div className="space-y-3">
          {TERMS.map((t, i) => (
            <p key={i} className="text-sm text-ink">
              <span className="font-medium text-ink-secondary">{i + 1}.</span> {t}
            </p>
          ))}
        </div>
      </Modal>

      {/* Forgot MPIN modal */}
      <Modal open={forgotOpen} onClose={() => setForgotOpen(false)} title="Forgot MPIN?" size="sm" footer={<div className="flex items-center justify-end border-t border-line px-5 py-4"><Button variant="secondary" onClick={() => setForgotOpen(false)}>Close</Button></div>}>
        <p className="text-sm text-ink">
          Please contact your administrator at{" "}
          <span className="font-medium text-primary-500">{platformSettings.supportEmail}</span> to reset your MPIN.
        </p>
      </Modal>

      {/* Image preview modal */}
      <Modal open={!!preview} onClose={() => setPreview(null)} title={preview?.title ?? ""} size="md" footer={<div className="flex items-center justify-end border-t border-line px-5 py-4"><Button variant="secondary" onClick={() => setPreview(null)}>Close</Button></div>}>
        {preview?.url && !preview.url.startsWith("data:application/pdf") ? (
          <img src={preview.url} alt={preview.title} className="max-h-[60vh] w-full rounded-lg object-contain bg-[#0F172A]" />
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <FileText className="h-10 w-10 text-primary-500" />
            <p className="text-sm font-medium text-ink">{preview?.fileName || preview?.title}</p>
            <p className="text-xs text-ink-secondary">Document attached and verified.</p>
          </div>
        )}
      </Modal>
    </div>
  );
}